import React from 'react';
import { CircleCheck as CheckCircle, ArrowRight, Chrome as Home, Sparkles, Clock, ExternalLink, MapPinned, ShieldCheck } from 'lucide-react';
import { toDate } from '../Utils/time';
import './OrderSuccessModal.css';

interface OrderSuccessModalProps {
  orderData: {
    // legacy direct-order shape
    id?: number;
    total_price?: number;
    created_at?: string;
    food_item_name?: string;
    quantity?: number;
    // cart checkout shape
    order_id?: number;
    grand_total?: number;
    items?: Array<{ food_name: string; quantity: number; price: number; subtotal: number }>;
    // shared
    status: string;
    delivery_address?: string;
    pickup_address?: string;
    google_map_link?: string;
    delivery_type?: string;
    payment_method?: string;
    // kitchen delivery fields
    order_status?: string;
    delivery_note?: string;
    delivery_info?: {
      distance_km?: number;
      distance_miles?: number;
      eta?: string;
      delivery_address?: string;
      delivered_by?: string;
      google_map_directions?: string;
    };
    delivery_otp?: string;
    otp_instructions?: string;
  };
  onClose: () => void;
  onViewOrders?: () => void;
  onTrackOrder?: () => void;
}

const OrderSuccessModal: React.FC<OrderSuccessModalProps> = ({
  orderData,
  onClose,
  onViewOrders,
  onTrackOrder,
}) => {
  const orderId = orderData.order_id ?? orderData.id;
  const totalPrice = orderData.grand_total ?? orderData.total_price ?? 0;

  const formatDate = (dateString?: string): string => {
    if (!dateString) return new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    try {
      return toDate(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const formatDeliveryType = (type?: string): string => {
    if (type === 'Delivery_partner' || type === 'delivery_partner') return 'Kitchen Delivery';
    if (type === 'kitchen_delivery') return 'Kitchen Delivery';
    if (type === 'self_pickup') return 'Self Pickup';
    return type || 'Unknown';
  };

  const formatPaymentMethod = (method?: string): string => {
    if (method === 'cod') return 'Cash on Delivery';
    if (method === 'online') return 'Online Payment';
    return method || 'Unknown';
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="order-success-overlay" onClick={handleOverlayClick}>
      <div className="order-success-modal">
        {/* Animated Success Icon */}
        <div className="success-icon-container">
          <div className="success-icon-circle">
            <CheckCircle size={70} />
          </div>
          <div className="success-particles">
            <Sparkles className="particle particle-1" size={18} />
            <CheckCircle className="particle particle-2" size={16} />
            <Sparkles className="particle particle-3" size={20} />
            <CheckCircle className="particle particle-4" size={14} />
          </div>
        </div>

        {/* Success Content */}
        <div className="success-content">
          <h2 className="success-title">Order Placed Successfully!</h2>
          <p className="success-subtitle">
            Your order has been confirmed and the cook has been notified.
            You'll receive updates about your order status.
          </p>
        </div>

        {/* Order Details */}
        <div className="order-details">
          <h3 className="order-details-title">Order Details</h3>
          <div className="order-details-grid">
            {orderId && (
              <div className="order-detail-card">
                <div className="order-detail-label">Order ID</div>
                <div className="order-detail-value order-id">#{orderId}</div>
              </div>
            )}
            <div className="order-detail-card">
              <div className="order-detail-label">Total Price</div>
              <div className="order-detail-value price">£{totalPrice.toFixed(2)}</div>
            </div>
            <div className="order-detail-card">
              <div className="order-detail-label">Status</div>
              <div className="order-detail-value status">{orderData.status}</div>
            </div>
            <div className="order-detail-card">
              <div className="order-detail-label">Order Date</div>
              <div className="order-detail-value">{formatDate(orderData.created_at)}</div>
            </div>

            {/* Single-item legacy fields */}
            {orderData.food_item_name && (
              <div className="order-detail-card">
                <div className="order-detail-label">Item</div>
                <div className="order-detail-value">{orderData.food_item_name}</div>
              </div>
            )}
            {orderData.quantity && !orderData.items && (
              <div className="order-detail-card">
                <div className="order-detail-label">Quantity</div>
                <div className="order-detail-value">{orderData.quantity}</div>
              </div>
            )}

            {orderData.delivery_type && (
              <div className="order-detail-card">
                <div className="order-detail-label">Delivery Type</div>
                <div className="order-detail-value">{formatDeliveryType(orderData.delivery_type)}</div>
              </div>
            )}
            {orderData.payment_method && (
              <div className="order-detail-card">
                <div className="order-detail-label">Payment Method</div>
                <div className="order-detail-value">{formatPaymentMethod(orderData.payment_method)}</div>
              </div>
            )}

            {orderData.delivery_address && (
              <div className="order-detail-card order-detail-card--full">
                <div className="order-detail-label">Delivery Address</div>
                <div className="order-detail-value">{orderData.delivery_address}</div>
              </div>
            )}
            {orderData.pickup_address && (
              <div className="order-detail-card order-detail-card--full">
                <div className="order-detail-label">Pickup Address</div>
                <div className="order-detail-value">{orderData.pickup_address}</div>
                {orderData.google_map_link && (
                  <a
                    href={orderData.google_map_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="order-map-link"
                  >
                    <ExternalLink size={13} />
                    View on Google Maps
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Cart items summary */}
          {orderData.items && orderData.items.length > 0 && (
            <div className="success-items-list">
              <h4 className="success-items-title">Items Ordered</h4>
              {orderData.items.map((item, idx) => (
                <div key={idx} className="success-item-row">
                  <span className="success-item-name">{item.food_name}</span>
                  <span className="success-item-qty">x{item.quantity}</span>
                  <span className="success-item-subtotal">£{item.subtotal.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Kitchen delivery confirmation note */}
        {(orderData.order_status === 'waiting_kitchen_confirmation' || orderData.delivery_note) && (
          <div className="order-note-card order-note-card--warning">
            <div className="order-note-label">Delivery Note</div>
            <div className="order-note-value">
              {orderData.delivery_note || 'Kitchen owner will confirm if they can deliver. If declined, order will be converted to self pickup.'}
            </div>
          </div>
        )}

        {/* Delivery info from kitchen delivery response */}
        {orderData.delivery_info && (
          <div className="order-note-card order-note-card--success">
            <div className="order-note-label">Delivery Details</div>
            <div className="order-note-details">
              {orderData.delivery_info.delivered_by && <div>Delivered by: {orderData.delivery_info.delivered_by}</div>}
              {orderData.delivery_info.eta && <div>ETA: {orderData.delivery_info.eta}</div>}
              {orderData.delivery_info.distance_miles != null && <div>Distance: {orderData.delivery_info.distance_miles} miles</div>}
              {orderData.delivery_info.google_map_directions && (
                <a href={orderData.delivery_info.google_map_directions} target="_blank" rel="noopener noreferrer" className="order-map-link order-map-link--inline">
                  <ExternalLink size={13} />
                  View Directions
                </a>
              )}
            </div>
          </div>
        )}

        {/* Delivery OTP */}
        {orderData.delivery_otp && (
          <div className="order-otp-card">
            <div className="order-otp-header">
              <ShieldCheck size={20} />
              <span className="order-otp-title">Delivery OTP</span>
            </div>
            <div className="order-otp-code">{orderData.delivery_otp}</div>
            <div className="order-otp-instructions">
              {orderData.otp_instructions ||
                'Share this 4-digit OTP with the kitchen/delivery person ONLY when you receive/pick up your order. Do not share it before receiving the order.'}
            </div>
          </div>
        )}

        {/* Cancellation Policy Notice */}
        <div className="order-note-card order-note-card--warning">
          <div className="order-note-label">Cancellation Window</div>
          <div className="order-note-value">
            You can cancel this order within 1 minute of placing it. After 1 minute, cancellation is no longer possible.
          </div>
        </div>

        {/* Next Steps */}
        <div className="next-steps-section">
          <h3 className="next-steps-title">
            <Clock size={20} />
            What happens next?
          </h3>
          <div className="steps-list">
            {orderData.order_status === 'waiting_kitchen_confirmation' ? (
              <>
                <div className="step-item">
                  <div className="step-number">1</div>
                  <div className="step-text">Kitchen owner is reviewing your delivery request</div>
                </div>
                <div className="step-item">
                  <div className="step-number">2</div>
                  <div className="step-text">If accepted, the kitchen owner will deliver to your address</div>
                </div>
                <div className="step-item">
                  <div className="step-number">3</div>
                  <div className="step-text">If declined, your order will be converted to self-pickup and you will be notified</div>
                </div>
              </>
            ) : (
              <>
                <div className="step-item">
                  <div className="step-number">1</div>
                  <div className="step-text">Cook receives your order and starts preparation</div>
                </div>
                <div className="step-item">
                  <div className="step-number">2</div>
                  <div className="step-text">
                    {orderData.delivery_type === 'self_pickup'
                      ? `You'll be notified when ready for pickup at: ${orderData.pickup_address || "the cook's location"}`
                      : 'Kitchen owner will deliver to your address'}
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number">3</div>
                  <div className="step-text">
                    {orderData.payment_method === 'cod'
                      ? 'Pay cash when you receive your order'
                      : 'Payment already processed online'}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="success-actions">
          <button onClick={onClose} className="success-primary-btn">
            <Home size={20} />
            <span>Continue Browsing</span>
          </button>
          {onTrackOrder && (
            <button onClick={onTrackOrder} className="success-secondary-btn">
              <MapPinned size={20} />
              <span>Track Order</span>
            </button>
          )}
          {onViewOrders && !onTrackOrder && (
            <button onClick={onViewOrders} className="success-secondary-btn">
              <ArrowRight size={20} />
              <span>View My Orders</span>
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="success-footer">
          <p>
            Need help with your order? Contact us at{' '}
            <a href="mailto:Homecookt.com@gmail.com" className="support-link" target="_blank" rel="noreferrer">
              Homecookt.com@gmail.com
            </a>
            {' '}or{' '}
            <a
              href={`https://wa.me/447737024736?text=Hi, I need help with my order #${orderId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="support-link"
            >
              WhatsApp us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessModal;
